import type { KeyboardEvent, MouseEvent } from 'react';
import { twMerge } from 'tailwind-merge';
import ListItem from '@mui/material/ListItem';
import {
  useDateFormatPreference,
  useLabCurrency,
  useOrder,
} from '@witmetrics/data-store';
import { formatPrice } from '@witmetrics/utils/strings';
import { formatOrderName } from '@witmetrics/utils/orders';
import { formatDate } from '@witmetrics/utils/time';
import { getSum } from '@witmetrics/utils/numbers';
import { useAppState } from '@/providers/AppStateProvider';
import { useToggle } from '@/hooks/useToggle';
import Card from '@/components/Card';
import OrderStatusChip from '@/components/OrderStatusChip';
import { ChevronDownIcon, OrdersIcon } from '@/icons';
import OrderInfo from './OrderInfo';
import { classes, formatCount } from './utils';

export type OrdersListItemProps = {
  variant?: 'card' | 'flat';
  className?: string;
  orderId: number;
  onOpen?: () => void;
};

export default function OrdersListItem({
  variant = 'card',
  className,
  orderId,
  onOpen,
}: OrdersListItemProps) {
  const { onSelectOrder } = useAppState();
  const order = useOrder(orderId);
  const currency = useLabCurrency(order?.labId);
  const dateFormat = useDateFormatPreference();
  const [isExpanded, toggleExpanded] = useToggle();

  const handleSelect = (e: MouseEvent<HTMLButtonElement>) => {
    e.stopPropagation();
    onSelectOrder(orderId);
    onOpen?.();
  };

  const handleToggleKeyDown = (e: KeyboardEvent<HTMLDivElement>) => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      toggleExpanded();
    }
  };

  if (!order) return null;

  const count = order.orderProducts.length;
  const total = getSum(
    ...order.orderProducts.map((op) => parseFloat(op.price))
  );

  return (
    <ListItem disablePadding>
      <Card
        className={twMerge(
          variant === 'flat' ? classes.flatCard : classes.card,
          className
        )}>
        <div
          className={classes.header}
          role="button"
          tabIndex={0}
          onClick={() => toggleExpanded()}
          onKeyDown={handleToggleKeyDown}>
          <div className={classes.iconWrapper}>
            <OrdersIcon className={classes.icon} />
          </div>
          <div className={classes.content}>
            <div className={classes.titleWrapper}>
              <button className={classes.title} onClick={handleSelect}>
                {formatOrderName(order, true, true)}
              </button>
              <OrderStatusChip orderStatusId={order.orderStatusId} />
            </div>
            <div className={classes.subtitle}>
              <span>{formatDate(order.createdAt, dateFormat)}</span>
              <span className={classes.separator} aria-hidden="true">
                |
              </span>
              <span>{formatCount(count)}</span>
              <span className={classes.separator} aria-hidden="true">
                |
              </span>
              <span className={classes.total}>
                {formatPrice(total, currency)}
              </span>
            </div>
          </div>
          <ChevronDownIcon
            className={twMerge(
              classes.chevron,
              isExpanded && classes.chevronOpen
            )}
          />
        </div>
        {isExpanded && <OrderInfo orderId={orderId} />}
      </Card>
    </ListItem>
  );
}
